import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LLMHostingType, LLMModelConfig } from "@/lib/types";
import { type FC, useState, useEffect } from "react";
import React from "react";
import { PlusCircle, Trash2 } from "lucide-react";

interface LLMConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: LLMModelConfig) => void;
  config: LLMModelConfig | null;
}

export const LLMConfigModal: FC<LLMConfigModalProps> = ({
  isOpen,
  onClose,
  onSave,
  config,
}) => {
  const [formData, setFormData] = useState<LLMModelConfig>({
    id: "",
    name: "",
    hosting_type: "cerebrium",
    endpoint: "",
    token: "",
    attributes: {},
  });

  useEffect(() => {
    if (config) {
      setFormData(config);
    } else {
      setFormData({
        id: "",
        name: "",
        hosting_type: "cerebrium",
        endpoint: "",
        token: "",
        attributes: {},
      });
    }
  }, [config, isOpen]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleSelectChange = (value: LLMHostingType) => {
    setFormData((prev) => ({ ...prev, hosting_type: value }));
  };

  const handleAttributeChange = (index: number, key: string, value: string) => {
    const newAttributes = { ...formData.attributes };
    const oldKey = Object.keys(newAttributes)[index];
    delete newAttributes[oldKey];
    newAttributes[key] = value;
    setFormData((prev) => ({ ...prev, attributes: newAttributes }));
  };

  const addAttribute = () => {
    setFormData((prev) => ({
      ...prev,
      attributes: { ...prev.attributes, [`new_key_${Object.keys(prev.attributes).length}`]: "" },
    }));
  };

  const removeAttribute = (key: string) => {
    const newAttributes = { ...formData.attributes };
    delete newAttributes[key];
    setFormData((prev) => ({ ...prev, attributes: newAttributes }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            {config ? "Edit LLM Model Config" : "Create LLM Model Config"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="id" className="text-right">
                ID
              </Label>
              <Input id="id" value={formData.id} onChange={handleChange} className="col-span-3" disabled={!!config} />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                Name
              </Label>
              <Input id="name" value={formData.name} onChange={handleChange} className="col-span-3" />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="hosting_type" className="text-right">
                Hosting Type
              </Label>
              <Select onValueChange={handleSelectChange} value={formData.hosting_type}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select a hosting type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cerebrium">Cerebrium</SelectItem>
                  <SelectItem value="gcp">GCP</SelectItem>
                  <SelectItem value="azure_openai">Azure OpenAI</SelectItem>
                  <SelectItem value="vllm">vLLM</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="endpoint" className="text-right">
                Endpoint
              </Label>
              <Input id="endpoint" value={formData.endpoint} onChange={handleChange} className="col-span-3" />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="token" className="text-right">
                Token
              </Label>
              <Input id="token" type="password" value={formData.token} onChange={handleChange} className="col-span-3" />
            </div>

            <div className="grid grid-cols-4 items-start gap-4">
              <Label className="text-right pt-2">Attributes</Label>
              <div className="col-span-3 space-y-2">
                {Object.entries(formData.attributes).map(([key, value], index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      placeholder="Key"
                      value={key}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleAttributeChange(index, e.target.value, String(value))}
                    />
                    <Input
                      placeholder="Value"
                      value={String(value)}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleAttributeChange(index, key, e.target.value)}
                    />
                    <Button type="button" variant="outline" size="icon" onClick={() => removeAttribute(key)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" onClick={addAttribute}>
                  <PlusCircle className="h-4 w-4 mr-2" />
                  Add Attribute
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
